import { parameterize } from '@moda/portal-stanchions';

export type PathParams = { [param: string]: string | undefined };

type CompiledSegment =
  | { type: 'static'; value: string }
  | {
      type: 'param';
      name: string;
      optional: boolean;
      constraint?: string;
      matcher?: RegExp;
    };

type CompiledPattern = {
  segments: CompiledSegment[];
};

const TOKEN_REGEX = /^:([A-Za-z0-9_]+)(?:\(([^)]*)\))?(\?)?$/;
const compiledPatternCache = new Map<string, CompiledPattern>();

const compilePattern = (pattern: string): CompiledPattern => {
  const cached = compiledPatternCache.get(pattern);

  if (cached) return cached;

  const compiled: CompiledPattern = {
    segments: pattern.split('/').map(segment => {
      const tokenMatch = segment.match(TOKEN_REGEX);

      if (!tokenMatch) {
        return { type: 'static', value: segment };
      }

      const [, name, constraint, optionalModifier] = tokenMatch;

      return {
        type: 'param',
        name,
        optional: optionalModifier === '?',
        constraint,
        matcher: constraint ? new RegExp(`^(?:${constraint})$`) : undefined
      };
    })
  };

  compiledPatternCache.set(pattern, compiled);
  return compiled;
};

const validateParam = (name: string, value: string, constraint?: string, matcher?: RegExp) => {
  if (!constraint || !matcher) {
    return;
  }

  if (!matcher.test(value)) {
    throw new Error(`Expected "${name}" to match "${constraint}", but received "${value}"`);
  }
};

export const mapPathParams = (
  params: PathParams,
  shouldParameterize: boolean
): { [param: string]: string } => {
  return Object.entries(params).reduce<{ [param: string]: string }>((acc, [key, value]) => {
    if (value !== undefined) {
      acc[key] = shouldParameterize ? parameterize(value) : value;
    }

    return acc;
  }, {});
};

/**
 * This is an alternative to react-router's generatePath that is optimized for performance.
 */
export const generateFastPath = (pattern: string, params: { [param: string]: string }): string => {
  const { segments } = compilePattern(pattern);

  return segments
    .map(segment => {
      if (segment.type === 'static') {
        return segment.value;
      }

      const value = params[segment.name];

      if (value === undefined) {
        if (segment.optional) {
          return null;
        }

        throw new Error(`Expected "${segment.name}" to be defined`);
      }

      validateParam(segment.name, value, segment.constraint, segment.matcher);
      return encodeURIComponent(value);
    })
    .filter((segment): segment is string => segment !== null)
    .join('/');
};

export const canGenerateFastPath = (
  pattern: string,
  params: { [param: string]: string }
): boolean => {
  const { segments } = compilePattern(pattern);

  return segments.every(segment => {
    if (segment.type !== 'param') {
      return true;
    }

    const value = params[segment.name];

    if (value === undefined) {
      return segment.optional;
    }

    return !segment.matcher || segment.matcher.test(value);
  });
};
